import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@nike/nike-design-system-components';
import { Heading, HeadingLevel } from '@nike/web-a11y-ariakit-react';
import { MEASUREMENTS_UNITS } from '@nike/ciclp-config';
import { ArrowLeft } from '@nike/nike-design-system-icons';
import { useSafeMatchMedia } from '../../hooks';
import ImageMedia from '../imageMedia';
import styles from './styles.styl';
import { SLIDES_NUMBER_SUPPORTED } from './constants';
import { calculatePossibleSlideIndex, resolveImageMediaProps } from './helpers';
import { actionTypes, initialState, reducer } from './reducer';
import { UnderbustInputSlide } from './underbustInputSlide';
import { CupInputSlide } from './cupInputSlide';
import { ResultsSlide } from './resultsSlide';
import { ProgressBar } from '../progressBar';

export const BraSizeFinder = ({
  slides,
  urls,
  title,
  subtitle,
  translations = {},
  selectedUnits = MEASUREMENTS_UNITS.IMPERIAL,
  analyticsItemId,
  countryCode,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { activeSlideIndex, selectedUnderbust, selectedCup, result } = state;
  const desktopMediaQuery = useBreakpoint('gt', 'm');
  const isDesktop = useSafeMatchMedia(desktopMediaQuery);
  useEffect(() => {
    dispatch({ type: actionTypes.RESET });
  }, [selectedUnits]);

  const hasSupportedNumberOfSlides = slides?.length === SLIDES_NUMBER_SUPPORTED;

  if (!hasSupportedNumberOfSlides) {
    return null;
  }

  const isFirstSlide = activeSlideIndex === 0;
  const isLastSlide = activeSlideIndex === SLIDES_NUMBER_SUPPORTED - 1;

  const goToSlide = index => {
    dispatch({
      payload: calculatePossibleSlideIndex(index, slides.length),
      type: actionTypes.SET_SLIDE_INDEX,
    });
  };

  const goToNextSlide = () => goToSlide(activeSlideIndex + 1);

  const goToPrevSlide = () => goToSlide(activeSlideIndex - 1);

  const sizesUrlKey = result ? Object.values(result).join(',') : null;
  const resultsLink = sizesUrlKey ? urls?.[sizesUrlKey] : '';

  const slidesComponents = [
    <UnderbustInputSlide
      key="underbust"
      underbustValue={selectedUnderbust}
      onNextClick={goToNextSlide}
      onSelect={payload => {
        dispatch({
          payload,
          type: actionTypes.SELECT_UNDERBUST,
        });
      }}
      translations={translations}
      selectedUnits={selectedUnits}
      title={slides[0].titleProps?.text}
      copy={slides[0].subtitleProps?.text}
      countryCode={countryCode}
    />,
    <CupInputSlide
      key="cup"
      underbustValue={selectedUnderbust}
      cupValue={selectedCup}
      onNextClick={goToNextSlide}
      onSelect={payload => {
        dispatch({
          payload,
          type: actionTypes.SELECT_CUP,
        });
      }}
      translations={translations}
      selectedUnits={selectedUnits}
      title={slides[1].titleProps?.text}
      copy={slides[1].subtitleProps?.text}
      onBackButtonClick={goToPrevSlide}
      countryCode={countryCode}
    />,
    <ResultsSlide
      key="results"
      onRestartButtonClick={() => dispatch({ type: actionTypes.RESET })}
      alphaSize={result?.alpha}
      alphaSizeCaption={translations.bra_size_finder_alpha_caption}
      hybridSize={result?.hybrid}
      hybridSizeCaption={translations.bra_size_finder_hybrid_caption}
      shopLink={resultsLink}
      title={slides[2].titleProps?.text}
      copyText={slides[2].subtitleProps?.text}
      shopLinkText={translations.bra_size_finder_shop_link?.replace(
        '[hybrid]',
        result?.hybrid,
      )}
      restartText={translations.bra_size_finder_restart}
    />,
  ];

  return (
    <section>
      {title && (
        <HeadingLevel level={2}>
          <Heading className={styles.heading} data-testid="heading">
            {title}
          </Heading>
        </HeadingLevel>
      )}
      {subtitle && (
        <p className={styles.copy} data-testid="copy">
          {subtitle}
        </p>
      )}
      <div className={styles.wrapper} data-testid="wrapper">
        <div className={styles.imagesWrapper} data-testid="images-wrapper">
          {slides.map((slide, index) => {
            const isActiveSlide = activeSlideIndex === index;
            return (
              <div
                key={slide.id}
                className={styles.image}
                data-active-slide={isActiveSlide}
                data-last-slide={isLastSlide}
                data-testid="image"
              >
                <ImageMedia
                  {...resolveImageMediaProps(isDesktop, slide)}
                  landscapeFocalPoint={slide?.landscapeFocalPoint}
                  portraitFocalPoint={slide?.portraitFocalPoint}
                  altText={slide?.titleProps?.text}
                  customClass="full-height-image"
                />
              </div>
            );
          })}
        </div>
        {!isLastSlide && (
          <div
            className={styles.progressBarWrapper}
            data-testid="progress-bar-wrapper"
          >
            {!isFirstSlide && (
              <button
                type="button"
                className={styles.backButton}
                onClick={goToPrevSlide}
                data-qa="back-button"
                data-testid="back-button"
                aria-label={translations.back}
              >
                <ArrowLeft />
              </button>
            )}
            <ProgressBar
              stepsNumber={SLIDES_NUMBER_SUPPORTED - 1}
              currentStep={activeSlideIndex + 1}
            />
          </div>
        )}
        {slidesComponents.map((slideComponent, index) => {
          const isActiveSlide = activeSlideIndex === index;
          const placementId = `${analyticsItemId}_${index + 1}`;
          return (
            <div
              key={slideComponent.key}
              className={styles.slide}
              data-active={isActiveSlide}
              data-analytics-placement-id={placementId}
              data-analytics-has-landmark="true"
              data-qa={`bra-slide-${index}`}
              data-testid={`bra-slide-${index}`}
            >
              {slideComponent}
            </div>
          );
        })}
      </div>
    </section>
  );
};

BraSizeFinder.propTypes = {
  analyticsItemId: PropTypes.string,
  countryCode: PropTypes.string,
  selectedUnits: PropTypes.string,
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      landscapeFocalPoint: PropTypes.object,
      portraitFocalPoint: PropTypes.object,
      subtitleProps: PropTypes.shape({
        text: PropTypes.string,
      }),
      titleProps: PropTypes.shape({
        text: PropTypes.string,
      }),
    }),
  ),
  subtitle: PropTypes.string,
  title: PropTypes.string,
  translations: PropTypes.shape({
    back: PropTypes.string,
    bra_size_finder_alpha_caption: PropTypes.string,
    bra_size_finder_hybrid_caption: PropTypes.string,
    bra_size_finder_restart: PropTypes.string,
    bra_size_finder_select_placeholder: PropTypes.string,
    bra_size_finder_shop_link: PropTypes.string,
    next: PropTypes.string,
    units_cm: PropTypes.string,
    units_inch: PropTypes.string,
  }),
  urls: PropTypes.objectOf(PropTypes.string),
};
